// 47107 A7k: React side of the on-demand help (see helpCache.js). Components that used to read the whole catalogue synchronously
// read their own rows through these hooks; nothing is requested until `wanted` turns true.
import { useEffect, useReducer, useState } from 'react';
import { requestHelp, pickHelp, isHelpFetched, subscribeHelpRows } from './helpCache';

export const HOVER_INTENT_MS = 200;   // an (i) icon: the pointer must rest this long (or the icon is focused / clicked) before its key is read
export const DWELL_MS = 700;          // a page / tab guide: the user must stay this long on the page / tab; passing through asks for nothing

// Re-render when any help row lands.
function useHelpRows() {
  const [, bump] = useReducer(x => x + 1, 0);
  useEffect(() => subscribeHelpRows(bump), []);
}

// {entry, loading} for one key in the active language. `wanted` = the user has shown intent (or the content is visible anyway).
export function useHelpEntry(key, lang, wanted = true) {
  useHelpRows();
  useEffect(() => { if (wanted && key) requestHelp([key], lang); }, [wanted, key, lang]);
  return { entry: pickHelp(key, lang), loading: !!key && wanted && !isHelpFetched(key, lang) };
}

// Several keys at once (a page and its tab, the Guide's active icons): ONE request. Returns a lookup and "any still loading".
export function useHelpKeys(keys, lang, wanted = true) {
  useHelpRows();
  const id = (keys || []).filter(Boolean).join('|');
  useEffect(() => { if (wanted && id) requestHelp(id.split('|'), lang); }, [wanted, id, lang]);
  return { pick: k => pickHelp(k, lang), loading: !!id && wanted && id.split('|').some(k => !isHelpFetched(k, lang)) };
}

// true once `ms` have passed with the same `token` (a navigation changes the token and restarts the dwell: the answer is token-bound, so the
// first render on the NEW page never inherits "dwelled" from the previous one).
export function useDwell(ms, token, active = true) {
  const [done, setDone] = useState(null);
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setDone(token), ms);
    return () => clearTimeout(t);
  }, [ms, token, active]);
  return active && done === token;
}
